import { useState, useMemo } from "react";
import { useHistory, useParams } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import SearchTextField from "components/common/SearchTextField";
import AddButton from "components/common/AddButton";
import QuantitySelector from "pages/photo/components/QuantitySelector";

import {
  useReportState,
  useReportDispatch,
  addItem,
  removeItem,
  updateItemQuantity
} from "../../state";
import {
  LitterFunction,
  LITTER_FUNCTION_META,
  getCategoriesForFunction
} from "custom/litterFunctions";
import { useCategoriesJson } from "features/firebase/categories/CategoriesProvider";
import { linkToReportOverview } from "routes/report/links";
import { matchesSearchWithSynonyms } from "utils/synonymSearch";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box"
  },
  list: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(0, 2)
  },
  heading: {
    fontWeight: 400,
    fontSize: "1.5rem",
    padding: theme.spacing(2.4, 2, 1.6)
  },
  searchField: {
    margin: theme.spacing(0, 2, 1)
  },
  row: {
    display: "flex",
    alignItems: "center",
    minHeight: 40,
    padding: `${theme.spacing(1)}px 0`,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    "&:last-child": {
      borderBottom: "none"
    }
  },
  rowActive: {
    fontWeight: 700
  },
  rowInactive: {
    color: theme.palette.text.primary,
    fontWeight: 400
  },
  label: {
    flex: 1,
    fontSize: "1.125rem"
  },
  quantity: {},
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.grey[200]}`
  },
  itemCount: {
    textAlign: "center",
    fontSize: "0.85rem",
    color: "#495459",
    marginBottom: theme.spacing(1)
  }
}));

export default function ItemsListPage() {
  const classes = useStyles();
  const history = useHistory();
  const { fn } = useParams<{ fn: string }>();
  const litterFunction = fn as LitterFunction;

  const dispatch = useReportDispatch();
  const state = useReportState();
  const categoriesJson = useCategoriesJson();
  const [search, setSearch] = useState("");

  const meta = LITTER_FUNCTION_META[litterFunction];
  const availableCategories = getCategoriesForFunction(
    categoriesJson,
    litterFunction
  );

  const currentItems = useMemo(
    () => state.items[litterFunction] || [],
    [state.items, litterFunction]
  );

  // Merge custom items (not in categories JSON) into the available list
  const allCategories = useMemo(() => {
    const knownIds = new Set(availableCategories.map((c) => c.id));
    const customEntries = currentItems
      .filter(
        (i) =>
          !!i.category.id && !!i.category.label && !knownIds.has(i.category.id)
      )
      .map((i) => ({
        id: i.category.id as string,
        label: i.category.label as string,
        synonyms: [] as string[]
      }));
    return [...customEntries, ...availableCategories];
  }, [availableCategories, currentItems]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return allCategories;
    return allCategories.filter((c) =>
      matchesSearchWithSynonyms(search, c.label, c.synonyms)
    );
  }, [allCategories, search]);

  const getItemQuantity = (categoryId: string): number => {
    const item = currentItems.find((i) => i.category.id === categoryId);
    return item ? item.totalQuantity : 0;
  };

  const handleQuantityChange = (
    categoryId: string,
    label: string,
    newQuantity: number
  ) => {
    const existing = currentItems.find((i) => i.category.id === categoryId);
    if (!existing && newQuantity > 0) {
      dispatch(addItem(litterFunction, { id: categoryId, label }));
      if (newQuantity > 1) {
        dispatch(updateItemQuantity(litterFunction, categoryId, newQuantity));
      }
    } else if (existing && newQuantity === 0) {
      dispatch(removeItem(litterFunction, categoryId));
    } else if (existing) {
      dispatch(updateItemQuantity(litterFunction, categoryId, newQuantity));
    }
  };

  // Total items across all functions
  const totalItems = Object.values(state.items).reduce(
    (sum, items) => sum + items.reduce((s, i) => s + i.totalQuantity, 0),
    0
  );

  const handleContinue = () => {
    history.push(linkToReportOverview());
  };

  return (
    <div className={classes.wrapper}>
      <Typography className={classes.heading}>{meta?.label}</Typography>
      <SearchTextField
        value={search}
        onChange={setSearch}
        placeholder={`Search items in "${meta?.label}"`}
        className={classes.searchField}
      />
      <div className={classes.list}>
        {filteredCategories.map(({ id, label }) => {
          if (!id || !label) return null;
          const qty = getItemQuantity(id);
          const isActive = qty > 0;

          return (
            <div
              className={classes.row}
              key={id}
              onClick={
                !isActive ? () => handleQuantityChange(id, label, 1) : undefined
              }
              style={!isActive ? { cursor: "pointer" } : undefined}
            >
              <Typography
                className={`${classes.label} ${
                  isActive ? classes.rowActive : classes.rowInactive
                }`}
              >
                {label}
              </Typography>
              {isActive && (
                <QuantitySelector
                  quantity={qty}
                  setQuantity={(q: number) =>
                    handleQuantityChange(id, label, q)
                  }
                  className={classes.quantity}
                />
              )}
            </div>
          );
        })}
        {filteredCategories.length === 0 && search.trim() && (
          <AddButton
            onClick={() => {
              const label = search.trim();
              const id = `custom-${label.toLowerCase().replace(/\s+/g, "-")}`;
              handleQuantityChange(id, label, 1);
              setSearch("");
            }}
          >
            Add "{search.trim()}"
          </AddButton>
        )}
      </div>
      <div className={classes.footer}>
        <Typography className={classes.itemCount}>
          {totalItems === 0
            ? "Please select litter above to report"
            : `${totalItems} items in this report`}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          fullWidth
          disableElevation
          disabled={totalItems === 0}
          onClick={handleContinue}
        >
          Continue
        </Button>
      </div>
    </div>
  );
}
